/**
 * Teknik kütüphanesi için mini tahta örnekleri.
 * Her tekniğin görsel açıklaması için 9x9 board verisi.
 */

export interface BoardCell {
  v?: number;       // Yerleştirilen değer
  c?: number[];     // Aday sayılar
  type?: 'given' | 'focus' | 'elim' | 'result';
  xc?: number[];    // Elinen adaylar (c'nin alt kümesi)
}

export type BoardData = (BoardCell | null)[][];

// Yardımcı fonksiyonlar
const g = (v: number): BoardCell => ({ v, type: 'given' });
const n = (...c: number[]): BoardCell => ({ c });                          // normal aday hücresi
const f = (...c: number[]): BoardCell => ({ c, type: 'focus' });          // tekniğin anahtar hücresi
const e = (c: number[], ...xc: number[]): BoardCell => ({ c, type: 'elim', xc }); // elim hücresi
const r = (...c: number[]): BoardCell => ({ c, type: 'result' });         // sonuç hücresi (yerleştirilecek)

export interface TechniqueExample {
  data: BoardData;
}

export const TECHNIQUE_BOARDS: Record<string, TechniqueExample> = {

  'naked-single': {
    data: [
      [g(2), g(6), g(7), g(9), g(5), g(1), g(8), g(3), g(4)],
      [g(1), g(3), g(4), g(2), g(6), g(8), g(5), g(7), g(9)],
      [g(5), g(8), g(9), g(3), g(4), g(7), g(1), g(2), g(6)],
      [g(3), g(4), g(1), g(7), g(2), g(6), g(9), g(8), g(5)],
      [g(9), g(7), g(8), g(4), g(3), g(5), g(6), g(1), g(2)],
      [g(6), g(2), g(5), n(1,8), r(8), g(9), g(3), n(4), n(7)],
      [n(4), g(5), n(3), g(6), g(1), n(2,3,4), g(7), n(4,9), n(8)],
      [n(4,7), n(1), g(6), n(8), g(9), n(2,4), n(2,4), g(5), g(3)],
      [g(8), n(9), g(2), n(5), n(7), n(3,4), n(4), n(4,6,9), g(1)],
    ],
  },
  'hidden-single': {
    data: [
      [n(1,9), g(5), n(1,9), g(2), g(3), g(7), g(4), r(1,8), g(6)],
      [n(1,2), g(3), g(4), g(6), g(8), g(9), n(1,5), n(1,5), g(7)],
      [g(6), g(7), g(8), g(1), g(4), g(5), g(2), g(3), g(9)],
      [n(1,9), g(2), g(6), g(8), g(5), g(4), n(1,9), g(7), n(1,3)],
      [g(3), g(4), g(5), g(9), n(1,7), g(2), g(6), n(1,8), n(1,8)],
      [n(1,7,9), g(8), n(1,9), g(3), n(1,7), g(6), n(1,5,9), g(2), n(1,4)],
      [g(4), g(1), g(3), g(5), g(9), g(8), g(7), g(6), g(2)],
      [g(5), g(6), g(7), g(4), g(2), g(3), n(1,8,9), n(1,8,9), n(1,8)],
      [g(8), g(9), g(2), g(7), g(6), g(1), g(3), g(4), g(5)],
    ],
  },
  'naked-pair': {
    data: [
      [g(3), g(9), g(5), g(8), g(7), g(4), g(6), g(2), g(1)],
      [g(1), g(2), g(4), g(3), g(5), g(6), g(7), g(8), g(9)],
      [g(6), g(7), g(8), g(1), g(2), g(9), g(3), g(4), g(5)],
      [g(2), g(4), n(1,9), g(5), g(6), g(3), g(8), n(1,9), g(7)],
      [g(5), g(3), g(6), f(7,9), g(8), n(1,7), g(2), n(1,9), g(4)],
      [n(7,9), g(8), n(1,7,9), e([2,4,9],9), n(4,9), n(1,2), g(5), g(3), g(6)],
      [g(4), g(1), n(2,7), e([2,6,7],7), g(3), g(5), g(9), n(6,7), g(8)],
      [g(8), g(5), n(2,7,9), e([2,4,6,7,9],7,9), n(4,9), n(2,7), g(1), n(6,7), g(3)],
      [n(7,9), g(6), g(3), f(7,9), g(1), g(8), g(4), g(5), g(2)],
    ],
  },
  'naked-triple': {
    data: [
      [g(2), g(4), g(8), g(9), g(3), n(1,5), n(1,5), g(7), g(6)],
      [g(1), g(3), g(5), g(6), g(7), g(8), g(2), g(4), g(9)],
      [g(6), g(7), g(9), n(1,2), n(1,2,4), n(1,4,5), g(3), n(5,8), n(1,8)],
      [g(3), g(5), g(1), g(7), g(8), g(2), g(9), g(6), g(4)],
      [g(9), g(8), g(4), n(1,3), g(6), n(1,3), g(7), g(2), g(5)],
      [g(7), g(2), g(6), g(4), g(5), g(9), g(8), g(1), g(3)],
      [f(4,5), g(6), g(2), e([1,3,8]), f(1,4), e([1,3,4,7]), f(1,5), g(9), e([1,7,8])],
      [n(4,5), g(9), g(3), n(1,2,8), n(1,2,4), n(1,4,7), g(6), n(5,8), n(1,7,8)],
      [g(8), g(1), g(7), g(5), g(9), g(6), g(4), g(3), g(2)],
    ],
  },
  'naked-quad': {
    data: [
      [g(9), n(2,5), n(4,7), g(6), n(1,3,6,8), g(3), n(2,5), n(4,7), g(7)],
      [g(4), n(3,6), n(1,5), g(8), n(2,6,8),   g(7), n(4,5), n(1,3), g(9)],
      [g(8), n(2,7), n(4,6), g(9), n(1,5,8),   g(4), n(3,6), n(2,7), g(5)],
      [g(3), n(4,9), n(2,5), g(7), n(3,7,8),   g(5), n(1,6), n(4,9), g(2)],
      [
        e([2,4,1,3], 1, 3),
        f(1, 3),
        f(1, 6, 8),
        e([5,1,3,6,8], 1, 3, 6, 8),
        f(3, 8),
        e([7,1,6,8], 1, 6, 8),
        f(1, 6),
        e([2,4,1,3,6,8], 1, 3, 6, 8),
        e([9,1,8], 1, 8),
      ],
      [g(6), n(3,5), n(2,7), g(1), n(1,4,6), g(9), n(5,8), n(3,9), g(3)],
      [g(7), n(2,8), n(5,9), g(4), n(3,6,7), g(2), n(2,4), n(5,8), g(1)],
      [g(5), n(4,7), n(1,6), g(3), n(2,5,8), g(8), n(3,7), n(4,6), g(4)],
      [g(2), n(1,9), n(3,8), g(5), n(4,6,9), g(6), n(2,5), n(1,7), g(8)],
    ],
  },
  'hidden-pair': {
    data: [
      [g(1), g(9), g(4), n(6,8), n(2,5,8), g(3), n(2,6), g(7), n(5,8)],
      [g(2), n(3,7), g(5), f(1,6,7,8), n(7,8,9), n(7,9), f(1,3,6), g(4), n(8,9)],
      [g(6), n(3,7), g(8), n(1,7), g(4), n(2,5,7,9), n(1,2,3), n(1,3), n(5,9)],
      [g(3), g(2), g(1), g(4), n(5,7), n(5,7), g(8), g(9), g(6)],
      [g(8), g(4), g(6), g(9), g(3), g(1), g(5), g(2), g(7)],
      [g(9), g(5), g(7), g(2), g(6), g(8), n(1,3), n(1,3), g(4)],
      [g(4), g(6), g(3), g(5), n(2,9), n(2,9), g(7), g(8), g(1)],
      [g(5), g(1), g(2), n(7,8), n(7,8), g(4), g(9), g(6), g(3)],
      [g(7), g(8), g(9), g(3), g(1), g(6), g(4), g(5), g(2)],
    ],
  },
  'hidden-triple': {
    data: [
      [g(3), g(8), g(6), n(2,5), n(1,4), g(7), f(1,2,5), n(4,5), g(9)],
      [g(1), g(2), g(4), n(3,5), g(6), g(9), n(5,7), n(3,5,7), g(8)],
      [g(5), g(7), g(9), n(2,3), n(1,4), g(8), f(1,2,6), n(3,4), f(2,4,6)],
      [n(2,8), n(4,5), g(1), g(6), g(7), g(3), n(2,5,8), g(9), n(2,4)],
      [n(2,8), n(4,5,6), g(3), g(1), g(9), n(2,5), n(2,5,6,8), n(4,5,8), g(7)],
      [g(9), n(5,6), g(7), g(4), g(8), n(2,5), g(3), g(1), n(2,6)],
      [g(4), g(3), g(8), g(7), g(2), g(1), g(9), g(6), g(5)],
      [g(6), g(1), g(2), g(9), g(5), g(4), n(7,8), n(7,8), g(3)],
      [g(7), g(9), g(5), g(8), g(3), g(6), g(4), g(2), g(1)],
    ],
  },
  'hidden-quad': {
    data: [
      [g(3), g(2), n(6,9), n(4,6), g(7), g(5), g(1), g(8), n(4,9)],
      [g(1), n(4,9), g(5), f(2,3,8), n(3,8), n(6,8), n(2,6), g(7), n(4,9)],
      [n(4,6), g(7), g(8), f(1,2,4), n(1,4), g(9), n(2,6), g(3), g(5)],
      [g(2), n(5,6,8), g(1), n(4,5,6,8), n(4,5,8), g(3), g(9), n(4,5), g(7)],
      [g(7), n(5,8), g(3), n(4,5,8), g(9), g(1), n(4,5,8), g(2), g(6)],
      [n(5,6,9), n(5,6,8,9), g(4), n(5,6,7,8), g(2), n(6,7,8), g(3), n(1,5), n(1,8)],
      [n(4,5,6,9), n(4,5,6,9), n(6,9), f(1,3,5,8,9), n(1,3,5,8), g(2), g(7), n(1,4,5,6), n(1,8)],
      [n(4,5,6,9), g(3), g(7), f(1,5,8,9), n(1,5,8), n(4,8), n(4,5,8), n(1,4,5,6), g(2)],
      [g(8), g(1), g(2), n(5,7), g(6), n(4,7), n(4,5), g(9), g(3)],
    ],
  },
  'pointing': {
    data: [
      [g(8), g(2), g(9), g(4), g(3), g(1), g(5), g(6), g(7)],
      [g(1), g(3), n(4,6), g(5), n(6,9), g(7), g(2), g(8), n(4,9)],
      [g(5), n(4,6), g(7), g(2), g(8), n(6,9), g(1), g(3), n(4,9)],
      [g(2), g(7), g(1), n(3,6,8), g(4), e([3,6,8],6,8), g(9), g(5), n(6,8)],
      [g(6), g(8), g(5), g(9), g(1), g(2), g(7), g(4), g(3)],
      [g(4), g(9), g(3), n(6,8), g(7), g(5), n(6,8), g(1), g(2)],
      [g(3), n(4,6), g(2), g(1), n(6,9), f(4,6,8,9), n(6,8), g(7), g(5)],
      [g(7), g(5), g(8), n(3,6), g(2), n(3,6), g(4), g(9), g(1)],
      [g(9), g(1), n(4,6), g(7), g(5), f(4,6,8), g(3), g(2), n(6,8)],
    ],
  },
  'box-line-reduction': {
    data: [
      [g(8), g(2), g(9), g(4), g(3), g(1), g(5), g(6), g(7)],
      [g(1), g(3), n(4,6), g(5), n(6,9), g(7), g(2), g(8), n(4,9)],
      [g(5), n(4,6), g(7), g(2), g(8), n(6,9), g(1), g(3), n(4,9)],
      [g(2), g(7), g(1), n(3,6,8), g(4), n(3,6), g(9), g(5), n(6,8)],
      [g(6), g(8), g(5), g(9), g(1), g(2), g(7), g(4), g(3)],
      [g(4), g(9), g(3), n(6,8), g(7), g(5), n(6,8), g(1), g(2)],
      [g(3), n(4,6), g(2), g(1), e([6,9],6), e([4,6,8,9],6), n(6,8), g(7), g(5)],
      [g(7), g(5), g(8), f(3,6), g(2), f(3,6), g(4), g(9), g(1)],
      [g(9), g(1), n(4,6), g(7), g(5), e([4,6,8],6), g(3), g(2), n(6,8)],
    ],
  },
  'x-wing': {
    data: [
      [g(8), g(7), g(5), g(2), g(9), g(1), g(6), g(4), g(3)],
      [g(1), g(2), g(3), n(4,6), n(4,6), g(8), g(5), g(7), g(9)],
      [g(4), n(6,9), n(6,9), g(3), g(5), g(7), g(1), g(2), g(8)],
      [f(2,6), g(4), e([1,2,6],6), g(7), g(3), g(5), g(9), g(8), f(1,6)],
      [g(3), g(5), n(1,6), g(8), g(2), g(9), g(4), n(1,6), g(7)],
      [g(9), g(8), g(7), n(1,4), n(1,4), g(6), g(2), g(3), g(5)],
      [g(5), g(1), g(8), n(6,9), g(7), g(2), g(3), n(6,9), g(4)],
      [f(2,6), g(3), e([2,6,9],6), e([1,6,9],6), g(8), g(4), g(7), g(5), f(1,6)],
      [g(7), n(6,9), g(4), g(5), n(1,6), g(3), g(8), n(1,6,9), g(2)],
    ],
  },
  'swordfish': {
    data: [
      [n(7,8), g(4), n(6,8), f(2,6), g(3), f(2,7), g(9), g(1), g(5)],
      [g(1), n(2,3), n(3,5), n(4,5), g(8), g(9), n(2,4), g(6), g(7)],
      [n(5,7), n(2,6), g(9), g(1), n(4,6), n(5,7), n(2,4,8), g(3), n(2,4,8)],
      [g(2), n(3,6), g(1), n(3,4,5,8), n(4,5,6), n(3,4,5,8), g(7), g(9), n(4,6,8)],
      [n(5,8), g(7), n(5,6,8), f(2,4,6), g(9), n(1,4), n(1,4,5,6), f(2,4,5), g(3)],
      [g(4), g(9), n(3,5,6), g(7), n(1,2,6), e([1,2,3,8],2), n(1,2,5,6,8), n(5,8), n(1,2,6,8)],
      [n(3,9), g(5), n(2,7), n(3,4,8), n(1,2,4), g(6), n(2,3,4,8), n(4,7,8), n(1,9)],
      [g(6), g(1), n(2,7), g(9), n(2,4,5), e([2,3,4,8],2), n(2,3,4,5,8), n(4,5,7,8), n(2,4,8)],
      [n(3,9), g(8), g(4), f(2,3,5), g(7), f(1,2,3,5), n(1,6), f(2,5), n(1,6,9)],
    ],
  },
  'jellyfish': {
    data: [
      [g(6), f(1,2), f(1,2,4), g(8), f(2,4), g(5), g(7), g(3), g(9)],
      [n(2,5), g(3), e([2,5,9],2), g(6), g(7), n(2,9), g(1), g(4), g(8)],
      [g(7), n(8,9), n(4,8,9), g(1), g(3), n(4,9), g(2), g(5), g(6)],
      [g(1), g(6), f(2,5), f(2,5), g(8), g(3), g(9), g(7), g(4)],
      [g(8), g(4), g(3), g(9), g(6), g(7), g(5), g(2), g(1)],
      [g(9), f(2,5,7), f(2,5,7), f(2,4,5), f(2,4,5), g(1), g(6), g(8), g(3)],
      [g(3), n(1,2,5,7,9), n(1,2,5,7,9), e([2,4,5,7],2), e([2,4,5],2), n(2,4), g(8), g(6), n(2,7)],
      [g(4), f(2,7), g(6), f(2,7), g(1), g(8), g(3), g(9), g(5)],
      [n(2,5), e([2,5,7,8],2), n(2,5,7,8), g(3), g(9), g(6), g(4), g(1), n(2,7)],
    ],
  },
  'xy-wing': {
    data: [
      [n(3,7), n(3,7), g(2), g(4), g(9), g(6), g(5), g(1), g(8)],
      [g(1), g(4), g(5), g(2), g(3), g(8), f(6,7), e([6,7]), g(9)],
      [g(6), g(8), g(9), g(1), g(5), g(7), g(2), g(3), g(4)],
      [g(2), g(1), g(6), g(7), g(4), g(3), g(9), g(8), g(5)],
      [g(8), n(3,7), n(3,4,7), g(5), g(6), g(9), n(1,4,7), g(2), n(1,7)],
      [g(9), g(5), n(4,7), g(8), g(1), g(2), g(3), n(4,7), g(6)],
      [n(3,4), g(6), n(1,3), g(9), g(7), n(1,4), g(8), g(5), g(2)],
      [g(5), g(2), n(1,7), g(6), g(8), n(1,4), f(4,7), g(9), g(3)],
      [n(4,7), g(9), g(8), g(3), g(2), g(5), e([1,4,6]), f(4,6), n(1,7)],
    ],
  },
  'xyz-wing': {
    data: [
      [g(5), g(3), n(7,8), f(7,8), g(1), g(9), g(6), g(4), g(2)],
      [g(1), g(2), g(4), n(3,6), g(5), n(3,6), g(7), g(8), g(9)],
      [g(6), n(7,9), n(7,8,9), g(2), n(4,8), n(4,7,8), g(1), g(3), g(5)],
      [g(2), n(1,4,6), n(1,6), n(3,4,8), n(4,6,8), n(3,4,6,8), g(9), g(5), g(7)],
      [g(7), n(4,6,9), n(6,9), g(5), g(2), n(4,6), g(3), g(1), g(8)],
      [g(3), g(8), g(5), g(9), g(7), g(1), g(2), g(6), g(4)],
      [n(4,9), g(5), g(2), g(1), g(3), f(4,7), g(8), n(7,9), g(6)],
      [g(8), n(1,6,7), n(1,6,7), e([6,7],7), g(9), g(5), g(4), g(2), g(3)],
      [n(4,9), n(6,7), g(3), f(4,7,8), n(4,6,8), g(2), g(5), n(7,9), g(1)],
    ],
  },
  'wxyz-wing': {
    data: [
      [g(5), g(1), g(3), g(2), g(9), g(8), g(4), g(7), g(6)],
      [g(2), g(4), g(6), n(1,7), n(1,3), n(3,7), g(5), g(8), g(9)],
      [g(7), g(8), g(9), f(4,6), g(5), n(4,6), g(1), g(2), g(3)],
      [g(1), g(6), g(2), f(4,8), n(3,4,8), n(3,4), g(9), g(5), g(7)],
      [g(3), g(5), g(4), g(9), g(7), g(2), g(6), g(1), g(8)],
      [n(8,9), n(7,9), n(7,8), g(5), g(6), g(1), g(2), g(3), g(4)],
      [n(4,6,8,9), g(3), n(1,5), f(1,4,6,8), n(4,8), n(4,5,6,9), g(7), n(4,9), g(2)],
      [n(4,6), g(2), n(1,7), g(3), f(1,4), n(4,6,7,9), g(8), n(4,9), g(5)],
      [n(4,8,9), n(7,9), n(5,7,8), e([4,7,8],4), g(2), n(4,5,9), g(3), g(6), g(1)],
    ],
  },
  'simple-coloring': {
    data: [
      [g(8), n(2,9), g(3), n(5,7,9), n(2,5,7), g(4), f(5,7), g(6), g(1)],
      [n(1,9), n(1,2,9), g(4), n(5,7,9), e([2,5,6,7],5), n(6,7,9), g(3), f(5,7), g(8)],
      [g(5), g(6), g(7), g(1), g(3), g(8), g(2), g(4), g(9)],
      [g(2), n(4,8), g(1), n(5,7,8), n(4,5,7), g(3), g(9), f(5,7), g(6)],
      [g(3), g(5), n(6,8), n(7,8,9), n(4,6,7), n(6,7,9), n(4,7), g(1), g(2)],
      [n(4,6), g(7), g(9), g(2), g(1), f(5,6), f(4,5), g(8), g(3)],
      [n(4,6), n(1,4), g(5), g(3), g(8), g(2), n(1,6), g(9), g(7)],
      [g(7), g(3), n(6,8), g(4), g(9), g(1), n(6,8), g(2), g(5)],
      [n(1,9), n(1,8,9), g(2), g(6), f(5,7), f(5,7), n(1,8), g(3), g(4)],
    ],
  },
  'remote-pairs': {
    data: [
      [f(3,7), g(5), g(6), g(4), g(1), g(8), e([3,7],3,7), g(9), g(2)],
      [g(4), g(9), g(1), g(2), g(6), g(3), g(7), g(8), g(5)],
      [g(2), n(1,8), g(8), g(5), n(2,9), g(9), f(3,7), g(4), g(6)],
      [g(1), g(2), g(3), g(6), g(4), g(5), g(8), g(7), g(9)],
      [g(5), g(4), g(7), g(8), g(9), g(1), g(2), g(6), g(3)],
      [g(6), g(8), g(9), g(3), g(2), g(7), g(4), g(5), g(1)],
      [g(3), g(1), g(4), g(7), g(5), g(6), f(3,7), g(2), g(8)],
      [g(8), g(6), g(2), g(9), n(1,4), g(4), n(1,6), g(1), n(5,7)],
      [f(3,7), n(2,9), g(5), g(1), n(3,8), g(2), e([3,7],3,7), g(3), g(4)],
    ],
  },
  'w-wing': {
    data: [
      [g(8), g(4), g(9), g(3), g(5), g(1), g(7), g(6), g(2)],
      [g(1), g(2), g(3), n(4,6,7,9), n(4,7,9), n(4,6,7,9), f(4,9), g(5), g(8)],
      [g(5), g(6), g(7), g(2), n(4,9), g(8), g(1), g(3), e([4,9],4,9)],
      [n(2,3), g(5), g(1), n(4,7), n(3,4,7), n(2,4,7), g(8), g(9), g(6)],
      [g(7), g(9), g(4), g(8), g(6), g(5), g(2), g(1), g(3)],
      [n(2,3), g(8), g(6), g(1), n(3,9), n(2,9), g(5), g(4), g(7)],
      [g(4), g(3), g(2), g(5), g(8), n(6,9), n(6,9), g(7), g(1)],
      [g(6), g(1), g(5), n(4,7,9), g(2), n(4,7,9), g(3), g(8), f(4,9)],
      [g(9), g(7), g(8), n(4,6), g(1), g(3), e([4,6],4), g(2), g(5)],
    ],
  },
  'skyscraper': {
    data: [
      [n(2,5,6), g(1), n(3,5), n(3,6,7), g(8), n(2,6,7), n(2,4,9), n(2,4,6,9), n(4,6,9)],
      [n(2,6), g(4), n(3,6), g(1), e([2,3,6],6), g(9), g(5), g(7), g(8)],
      [g(7), g(8), g(9), g(4), g(5), f(2,6), g(1), f(2,6), g(3)],
      [g(1), g(7), g(2), g(8), f(4,6), g(3), n(4,9), f(4,6,9), g(5)],
      [g(8), g(3), n(4,6), n(5,6), g(9), e([4,5,6],6), g(7), g(1), g(2)],
      [n(5,6), g(9), n(4,5,6), g(2), g(7), g(1), g(3), g(8), n(4,6)],
      [g(3), n(2,5), g(7), n(5,9), g(1), g(8), g(6), n(2,4,9), n(4,9)],
      [g(4), n(2,5,6), g(8), n(5,7,9), n(2,6), n(5,7), n(2,9), g(3), g(1)],
      [g(9), n(2,6), g(1), n(3,6), n(2,3,4,6), n(2,4,6), g(8), g(5), g(7)],
    ],
  },
  'two-string-kite': {
    data: [
      [g(4), n(5,8), n(3,6), g(9), g(1), n(5,8), g(2), f(3,6), g(7)],
      [n(1,5), g(2), n(3,6), g(4), n(5,6), g(7), n(1,3,6), g(8), g(9)],
      [g(7), n(1,8), g(9), g(2), g(3), f(6,8), f(1,6), g(4), g(5)],
      [g(2), g(6), g(1), n(5,8), n(5,8), g(3), g(9), g(7), g(4)],
      [g(8), g(7), g(4), g(1), g(9), g(2), n(3,6), g(5), n(3,6)],
      [g(3), g(9), g(5), g(6), g(7), g(4), g(8), g(1), g(2)],
      [n(5,6), n(3,5), g(2), g(7), n(6,8), g(1), g(4), g(9), n(3,6,8)],
      [n(1,6,9), n(1,3), g(7), n(3,8), g(4), n(6,9), g(5), g(2), n(1,3,6,8)],
      [n(1,6,9), g(4), g(8), n(3,5), g(2), e([5,6,9],6), g(7), f(3,6), n(1,3,6)],
    ],
  },
  'empty-rectangle': {
    data: [
      [g(5), g(3), g(1), g(9), g(2), g(8), g(6), g(4), g(7)],
      [g(2), g(4), g(6), g(1), g(3), g(7), g(5), g(8), g(9)],
      [g(7), g(8), g(9), g(4), g(5), g(6), g(1), g(2), g(3)],
      [g(1), g(2), g(3), g(5), g(4), f(9), g(7), g(6), g(8)],
      [g(4), g(5), n(5,7), g(6), g(8), f(7), f(7), g(9), g(2)],
      [g(6), g(9), g(8), g(2), g(7), n(2,3), g(4), g(1), g(5)],
      [g(3), g(1), g(2), g(7), g(9), g(4), g(8), g(5), g(6)],
      [g(8), n(6,7), n(3,7), e([3,7],7), g(1), f(7), g(9), g(3), g(4)],
      [g(9), n(6,7), g(4), g(8), g(6), n(2,5), n(2,7), g(7), g(1)],
    ],
  },
  'unique-rectangle': {
    data: [
      [n(3,7), n(3,7), g(2), g(4), g(9), g(6), g(5), g(1), g(8)],
      [g(1), g(4), g(5), g(2), g(3), g(8), f(6,7), f(6,7), g(9)],
      [g(6), g(8), g(9), g(1), g(5), g(7), g(2), g(3), g(4)],
      [g(2), g(1), g(6), g(7), g(4), g(3), g(9), g(8), g(5)],
      [g(8), n(3,7), n(3,4,7), g(5), g(6), g(9), n(1,4,7), g(2), n(1,7)],
      [g(9), g(5), n(4,7), g(8), g(1), g(2), g(3), n(4,7), g(6)],
      [n(3,4), g(6), n(1,3), g(9), g(7), n(1,4), g(8), g(5), g(2)],
      [g(5), g(2), n(1,7), g(6), g(8), n(1,4), n(4,7), g(9), g(3)],
      [n(4,7), g(9), g(8), g(3), g(2), g(5), f(1,4,6,7), f(4,6,7), n(1,7)],
    ],
  },
  'hidden-unique-rectangle': {
    data: [
      [g(5), g(4), g(7), g(9), g(3), g(8), g(6), g(2), n(1,7)],
      [g(9), g(6), g(8), g(1), g(7), g(2), g(4), g(5), g(3)],
      [g(2), g(3), f(2,7), g(4), g(5), g(6), f(1,2,7), g(8), g(9)],
      [g(1), g(2), g(3), g(5), g(4), g(7), g(8), g(9), g(6)],
      [g(4), g(5), g(6), g(2), g(8), g(9), g(3), g(7), g(1)],
      [g(7), g(8), g(9), g(3), g(6), g(1), g(2), g(4), g(5)],
      [g(3), g(1), f(2,7), g(7), g(9), g(4), f(2,5,7), g(6), g(8)],
      [g(6), g(7), n(2,4), g(8), g(1), g(5), g(9), g(3), n(2,4)],
      [g(8), g(9), n(1,4), g(6), g(2), g(3), n(1,5,7), g(1), n(4,5)],
    ],
  },
  'extended-unique-rectangle': {
    data: [
      [g(4), g(7), g(9), g(6), g(1), g(5), g(2), n(3,8), n(3,8)],
      [g(6), f(3,8), f(3,8), g(2), g(9), g(4), f(3,8), g(1), g(7)],
      [g(1), g(2), g(5), g(3), g(7), g(8), g(4), g(6), g(9)],
      [g(2), n(1,3), g(3), n(5,9), g(5), n(6,7), g(8), g(9), g(6)],
      [g(5), f(3,8), f(3,8), g(8), g(3), g(9), r(3,5,8), g(7), g(2)],
      [g(8), g(9), g(7), g(1), g(2), g(6), g(3), n(4,5), n(4,5)],
      [g(3), n(4,6), g(1), g(7), g(6), g(2), g(9), n(5,8), n(4,8)],
      [g(7), g(8), g(2), g(9), g(4), g(1), g(6), n(3,5), g(3)],
      [g(9), n(4,6), g(4), g(5), g(8), g(3), n(1,7), n(2,5), g(1)],
    ],
  },
  'rectangle-elimination': {
    data: [
      [g(2), g(4), g(8), g(9), g(3), f(1,5), f(1,5), g(7), g(6)],
      [g(1), g(3), g(5), g(6), g(7), g(8), g(2), g(4), g(9)],
      [g(6), g(7), g(9), n(1,2), n(1,2,4), n(1,4,5), g(3), n(5,8), n(1,8)],
      [g(3), g(5), g(1), g(7), g(8), g(2), g(9), g(6), g(4)],
      [g(9), g(8), g(4), n(1,3), g(6), n(1,3), g(7), g(2), g(5)],
      [g(7), g(2), g(6), g(4), g(5), g(9), g(8), g(1), g(3)],
      [n(4,5), g(6), g(2), n(3,8), n(1,4), n(3,7), n(1,5), g(9), n(7,8)],
      [n(4,5), g(9), g(3), n(1,2,8), n(1,2,4), f(1,4,7), g(6), n(5,8), n(1,7,8)],
      [g(8), g(1), g(7), g(5), g(9), g(6), g(4), g(3), g(2)],
    ],
  },
  'bug-plus-1': {
    data: [
      [g(8), g(7), g(5), g(2), g(9), g(1), g(6), g(4), g(3)],
      [g(1), g(2), g(3), n(4,6), n(4,6), g(8), g(5), g(7), g(9)],
      [g(4), n(6,9), n(6,9), g(3), g(5), g(7), g(1), g(2), g(8)],
      [n(2,6), g(4), n(1,2), g(7), g(3), g(5), g(9), g(8), n(1,6)],
      [g(3), g(5), n(1,6), g(8), g(2), g(9), g(4), n(1,6), g(7)],
      [g(9), g(8), g(7), n(1,4), n(1,4), g(6), g(2), g(3), g(5)],
      [g(5), g(1), g(8), n(6,9), g(7), g(2), g(3), n(6,9), g(4)],
      [n(2,6), g(3), n(2,9), n(1,9), g(8), g(4), g(7), g(5), n(1,6)],
      [g(7), n(6,9), g(4), g(5), n(1,6), g(3), g(8), r(1,6,9), g(2)],
    ],
  },
  'finned-x-wing': {
    data: [
      [g(1), n(2,4,9), f(2,4,9), e([4,9]), g(5), g(7), g(8), g(6), g(3)],
      [n(3,7), n(3,4,7,9), g(5), g(6), g(8), f(3,4,9), g(1), g(2), n(7,9)],
      [g(6), n(3,7,9), g(8), g(1), n(2,3,9), f(2,3,9), g(4), g(5), n(7,9)],
      [n(2,3), n(1,2,3,4), n(1,2,4), g(7), g(6), n(3,4), g(9), g(8), g(5)],
      [n(3,5,7), n(3,5,7), n(4,7), n(3,4,9), n(3,4,9), g(8), g(2), g(1), g(6)],
      [g(9), g(8), g(6), g(2), g(1), g(5), g(3), g(7), g(4)],
      [g(4), g(6), n(2,7), n(3,8), n(2,3,7), g(1), g(5), g(9), n(2,8)],
      [n(2,5), n(1,2,5,9), g(3), n(8,9), n(2,9), g(6), g(7), g(4), n(1,2,8)],
      [g(8), n(1,2,7,9), f(1,2,7,9), g(5), n(2,4,7,9), f(2,4,9), g(6), g(3), n(1,2)],
    ],
  },
  'finned-swordfish': {
    data: [
      [n(2,3,5), n(3,4,6), g(1), n(2,3,6,7), e([2,3,6,7]), g(8), n(4,5), g(9), n(2,5)],
      [f(2,3,5), n(4,6), f(2,3,6), f(1,2,3,6), f(1,2,3,6,9), n(1,2,6,9), n(4,5,8), g(7), n(1,2,5,8)],
      [g(7), g(8), g(9), n(1,2), g(4), g(5), n(1,2), g(3), g(6)],
      [f(1,3), g(2), f(3,6), g(8), g(5), n(1,6), g(9), g(4), g(7)],
      [n(1,9), n(1,6,9), g(5), n(1,2,6,7), n(1,2,6,7), g(4), n(1,2), g(8), g(3)],
      [g(4), g(7), g(8), g(9), n(1,2), g(3), g(6), n(1,2,5), n(1,2,5)],
      [f(1,2,3,8), g(5), f(2,3), g(4), f(1,3,6,8), n(1,2,6), g(7), n(1,2,6), g(9)],
      [g(6), n(1,3,9), g(7), n(1,2,3), n(1,2,3,8,9), n(1,2,9), n(3,5,8), n(1,2,5), g(4)],
      [n(1,2,3,8,9), n(1,3,9), g(4), g(5), n(1,2,3,6,8,9), g(7), n(3,8), n(1,2,6), n(1,2,8)],
    ],
  },
  'x-cycles': {
    data: [
      [g(4), n(5,8), n(3,6), g(9), g(1), n(5,8), g(2), n(3,6), g(7)],
      [n(1,5), g(2), n(3,6), g(4), f(5,6), g(7), n(1,3,6), g(8), g(9)],
      [g(7), n(1,8), g(9), g(2), g(3), f(6,8), f(1,6), g(4), g(5)],
      [g(2), g(6), g(1), n(5,8), n(5,8), g(3), g(9), g(7), g(4)],
      [g(8), g(7), g(4), g(1), g(9), g(2), f(3,6), g(5), f(3,6)],
      [g(3), g(9), g(5), g(6), g(7), g(4), g(8), g(1), g(2)],
      [n(5,6), n(3,5), g(2), g(7), f(6,8), g(1), g(4), g(9), f(3,6,8)],
      [n(1,6,9), n(1,3), g(7), n(3,8), g(4), n(6,9), g(5), g(2), n(1,3,6,8)],
      [n(1,6,9), g(4), g(8), n(3,5), g(2), n(5,9), g(7), n(3,6), n(1,3,6)],
    ],
  },
  'grouped-x-cycles': {
    data: [
      [g(9), n(2,4), g(3), n(1,8), f(6), n(1,7), g(5), n(2,8), n(4,7)],
      [g(7), n(1,5), g(8), n(3,6), n(2,4), n(1,9), n(3,6), n(1,4), g(2)],
      [n(1,4), g(6), n(2,5), n(4,9), n(3,7), n(2,8), n(1,9), n(3,4), n(5,7)],
      [g(3), n(2,7), n(1,4), n(5,7), f(2,6), g(9), n(1,7), n(2,5), g(8)],
      [n(2,5), n(4,8), g(9), n(1,3), f(2,6), n(1,4), n(3,8), g(7), n(3,5)],
      [n(1,8), n(3,4), g(6), n(2,8), n(4,9), n(1,5), n(2,4), n(1,9), n(3,7)],
      [g(5), n(1,9), n(1,7), n(2,6), g(3), n(2,4), n(2,4), g(6), n(1,9)],
      [g(6), e([5,6],6), n(2,5), n(4,7), f(6), n(3,8), n(1,4), n(3,5), n(1,9)],
      [n(2,4), n(3,7), n(4,7), n(1,9), n(1,5), g(6), g(8), n(2,5), n(3,4)],
    ],
  },
  '3d-medusa': {
    data: [
      [n(6,9), g(2), n(6,9), g(8), g(5), g(7), g(3), g(4), g(1)],
      [g(1), g(3), g(4), n(2,9), g(6), e([1,2,9],9), g(5), n(7,8,9), n(7,8,9)],
      [g(5), g(7), g(8), g(1), g(3), g(4), g(2), g(6), g(9)],
      [g(2), g(5), g(1), g(7), g(4), g(3), g(9), g(8), g(6)],
      [e([1,6,9],9), n(6,8,9), g(3), g(5), n(8,9), g(1), g(4), n(2,7,8), n(2,7,8)],
      [g(4), n(8,9), g(7), g(6), g(2), n(8,9), g(1), g(3), g(5)],
      [g(3), g(1), g(5), g(4), g(7), g(6), g(8), n(2,9), n(2,9)],
      [g(7), g(4), g(2), f(3,5,9), n(8,9), n(5,8,9), g(6), g(1), g(3)],
      [g(8), e([4,6,9],9), n(6,9), n(2,3,9), g(1), n(2,9), g(7), g(5), g(4)],
    ],
  },
  'xy-chain': {
    data: [
      [n(3,4), n(3,4), g(9), g(7), g(1), g(5), g(6), g(2), g(8)],
      [g(1), n(2,5), f(2,5), g(6), g(8), g(9), g(3), g(4), g(7)],
      [n(6,8), n(6,7), n(6,7,8), g(2), g(3), g(4), g(1), g(5), g(9)],
      [f(2,6), n(2,6), g(1), g(5), g(7), g(3), g(8), g(9), g(4)],
      [n(3,5,8), n(3,5), n(3,5,8), g(4), g(9), g(6), g(2), g(7), g(1)],
      [g(7), g(9), g(4), g(1), g(2), g(8), g(5), g(3), g(6)],
      [f(2,4), g(8), f(2,7), g(9), g(5), g(1), f(4,7), g(6), g(3)],
      [f(5,6), g(1), e([5,6]), g(3), g(4), g(7), g(9), g(8), g(2)],
      [g(9), n(3,4,7), n(3,7), g(8), g(6), g(2), n(4,7), g(1), g(5)],
    ],
  },
  'aic': {
    data: [
      [g(3), n(4,6), n(4,6), g(9), g(8), g(5), n(1,2,7), n(2,7), n(1,2,7)],
      [g(1), g(2), g(5), g(3), g(4), g(7), g(6), g(8), g(9)],
      [g(7), g(8), g(9), g(1), g(2), g(6), g(3), g(4), g(5)],
      [g(2), n(3,4,6), g(1), n(4,6), g(7), n(3,8,9), n(4,5,9), n(3,5), n(4,8)],
      [g(5), g(9), n(4,6), n(2,4,6), g(1), n(3,8), n(2,4,7), n(2,3,7), n(2,4,7,8)],
      [g(8), n(3,4), g(7), n(2,4), g(5), n(3,9), f(1,2,4,9), n(1,2,3), g(6)],
      [g(4), g(5), g(2), g(7), g(6), g(1), g(8), g(9), g(3)],
      [g(6), g(7), g(3), g(8), g(9), g(2), n(1,4,5), n(1,5), n(1,4)],
      [g(9), g(1), g(8), g(5), g(3), g(4), n(2,7), g(6), n(2,7)],
    ],
  },
  'aligned-pair-exclusion': {
    data: [
      [g(9), g(3), g(1), g(6), g(4), g(7), g(2), g(8), g(5)],
      [g(6), n(2,5), n(5,7), n(1,8), n(2,8), n(1,9), g(9), g(4), g(3)],
      [g(4), n(5,8), g(8), n(2,3), n(1,9), n(1,3), g(6), n(1,5), n(2,7)],
      [g(7), g(4), g(6), n(2,9), g(5), n(1,9), g(8), g(3), n(1,2)],
      [g(1), n(5,8), g(3), e([2,5,7],7), g(6), e([2,5,9],9), g(4), n(5,7), n(2,8)],
      [g(5), n(1,8), n(2,9), n(2,4), g(7), n(1,4), n(1,9), g(6), g(9)],
      [g(3), n(1,6), n(4,7), n(4,8), n(1,2), n(2,4), g(5), n(1,9), n(2,6)],
      [g(2), n(5,7), g(5), n(1,9), g(3), g(6), n(1,7), n(1,4), n(4,8)],
      [g(8), n(1,6), n(4,9), n(1,7), n(1,2), n(2,5), g(3), n(2,5), n(4,7)],
    ],
  },
  'almost-locked-sets': {
    data: [
      [n(3,4,6), n(2,5,6), g(8), n(3,5), n(1,4), g(9), n(1,2,5,7), n(2,4), n(6,7)],
      [g(1), e([2,5,9]), f(3,4,9), f(3,5), g(6), g(7), f(5,9), n(2,4,8), n(8,9)],
      [f(4,5,6), g(7), f(4,5,6,9), g(2), n(1,4), g(8), n(1,5,9), g(3), f(6,9)],
      [n(2,5), n(5,9), g(1), g(8), g(7), g(3), n(2,9), g(6), g(4)],
      [n(3,4,7), g(8), n(3,4), g(6), g(9), g(2), n(3,7), g(1), g(5)],
      [n(2,3,6,7), n(6,9), n(3,6,9), g(1), g(5), g(4), n(2,3,7,9), n(2,8), n(7,8,9)],
      [n(5,6), g(3), n(5,6), g(7), g(8), g(1), g(4), g(9), g(2)],
      [g(8), g(4), g(2), g(9), g(3), g(5), g(6), g(7), g(1)],
      [g(9), g(1), g(7), g(4), g(2), g(6), g(8), g(5), g(3)],
    ],
  },
  'death-blossom': {
    data: [
      [e([2,3,4,5,6],4), n(2,5,6), g(8), n(3,5), n(1,4), g(9), n(1,2,5,7), n(2,4), n(6,7)],
      [g(1), n(2,5,9), n(3,4,5,9), n(3,5), g(6), g(7), n(2,5,9), n(2,4,8), n(8,9)],
      [n(4,5,6), g(7), n(4,5,6,9), g(2), f(1,4), g(8), n(1,5,9), g(3), n(6,9)],
      [n(2,5), n(2,5,9), g(1), g(8), g(7), g(3), n(2,9), g(6), g(4)],
      [n(3,4,7), g(8), n(3,4), g(6), g(9), g(2), n(3,7), g(1), g(5)],
      [n(2,3,6,7), n(2,6,9), n(3,6,9), g(1), g(5), g(4), n(2,3,7,9), n(2,8), n(7,8,9)],
      [n(5,6), g(3), n(5,6), g(7), g(8), g(1), g(4), g(9), g(2)],
      [g(8), g(4), g(2), g(9), g(3), g(5), g(6), g(7), g(1)],
      [g(9), g(1), g(7), g(4), g(2), g(6), g(8), g(5), g(3)],
    ],
  },
  'digit-forcing-chains': {
    data: [
      [n(3,4,5,6), n(2,5,6), g(8), n(3,5), n(1,4), g(9), n(1,2,5,7), n(2,4), n(6,7)],
      [g(1), n(2,5,9), n(3,4,5,9), n(3,5), g(6), g(7), e([2,5,9]), n(2,4,8), n(8,9)],
      [n(4,5,6), g(7), n(4,5,6,9), g(2), n(1,4), g(8), n(1,5,9), g(3), n(6,9)],
      [n(2,5), n(5,9), g(1), g(8), g(7), g(3), n(2,9), g(6), g(4)],
      [n(3,4,7), g(8), n(3,4), g(6), g(9), g(2), n(3,7), g(1), g(5)],
      [n(2,3,6,7), n(6,9), n(3,6,9), g(1), g(5), g(4), n(2,3,7,9), n(2,8), n(7,8,9)],
      [n(5,6), g(3), n(5,6), g(7), g(8), g(1), g(4), g(9), g(2)],
      [g(8), g(4), g(2), g(9), g(3), g(5), g(6), g(7), g(1)],
      [g(9), g(1), g(7), g(4), g(2), g(6), g(8), g(5), g(3)],
    ],
  },
  'cell-forcing-chains': {
    data: [
      [g(5), n(2,9), g(7), n(3,6), g(4), n(1,6), g(8), n(1,9), n(2,3)],
      [n(1,4), g(6), g(3), n(2,8), n(2,9), n(1,7), n(1,5), n(4,9), n(1,5)],
      [n(2,8), n(1,9), g(4), g(5), n(1,7), g(3), n(1,6), g(2), n(6,7)],
      [g(6), n(3,7), n(1,2), f(1,5,8), g(9), n(2,4), n(4,7), n(3,5), n(2,4)],
      [n(3,9), n(4,5), n(1,9), n(2,7), g(6), n(1,8), n(2,5), n(7,8), n(1,3)],
      [g(7), g(8), n(1,5), n(1,3), n(3,5), g(2), g(9), n(1,4), g(6)],
      [n(1,8), n(2,4), n(2,6), n(1,9), n(1,8), g(5), r(3), n(6,8), n(4,7)],
      [n(3,9), g(5), g(8), n(4,6), g(2), n(1,4), n(1,6), g(7), n(1,9)],
      [n(1,4), g(3), n(1,6), n(4,7), n(5,7), g(9), n(2,4), n(5,8), n(2,8)],
    ],
  },
  'unit-forcing-chains': {
    data: [
      [g(5), g(9), g(3), f(8), n(1,6), n(2,7), g(4), n(1,7), n(1,6)],
      [g(6), n(1,4), n(4,7), n(1,2), f(8), n(1,9), n(2,5), g(3), n(1,7)],
      [n(1,4), n(2,7), g(8), n(1,4), n(2,6), f(8), n(1,3), n(5,7), n(1,5)],
      [n(3,8), g(5), n(1,6), g(9), n(4,7), g(2), n(1,6), g(8), n(3,4)],
      [g(9), n(1,4), n(2,6), g(7), n(3,5), g(6), n(1,3), n(1,5), n(2,4)],
      [n(2,7), n(3,6), n(1,4), n(3,5), n(1,4), n(4,5), n(1,7), n(1,5), r(2)],
      [g(7), n(1,8), n(1,9), g(6), n(3,9), n(1,3), g(5), n(2,4), n(3,8)],
      [n(1,3), g(6), g(5), n(2,8), n(1,4), n(1,7), n(2,8), g(9), n(3,4)],
      [n(2,4), n(3,7), n(2,9), g(5), g(8), n(3,4), n(1,6), n(1,7), n(1,6)],
    ],
  },
  'nishio-forcing-chains': {
    data: [
      [g(6), n(1,4), g(9), n(2,7), n(3,5), g(8), n(1,5), g(3), n(2,4)],
      [g(5), n(2,7), n(2,3), g(1), g(4), n(3,6), n(7,9), n(6,8), n(2,8)],
      [n(1,3), g(8), n(1,4), n(5,9), g(7), n(3,5), g(2), n(4,6), n(1,9)],
      [n(2,4), g(3), g(7), n(4,6), n(1,5), n(1,6), g(9), n(2,5), g(8)],
      [n(1,4), n(1,6), g(8), g(3), f(3,7), n(2,4), n(1,5), n(2,6), n(1,5)],
      [g(9), n(1,5), n(1,5), n(2,8), g(6), n(2,4), g(3), n(4,7), n(1,7)],
      [n(3,7), n(4,9), g(6), n(4,7), n(2,9), g(1), g(8), n(2,5), n(3,5)],
      [g(8), e([4,7],4,7), n(3,4), g(5), n(1,9), n(3,7), n(1,4), g(1), n(3,6)],
      [n(1,4), n(1,5), n(1,5), g(6), g(8), n(3,9), n(4,7), n(4,9), g(2)],
    ],
  },
  'pattern-overlay': {
    data: [
      [n(3,4), n(3,4,8,9), g(1), g(2), n(3,4,5,6,8,9), n(3,4,5,8,9), n(5,8), g(7), n(6,8)],
      [g(2), g(5), n(6,7,8), n(4,6,7), n(4,6,7,8), n(4,7,8), g(1), g(3), g(9)],
      [n(3,7), e([3,7,8,9]), n(3,6,7,8,9), n(1,5,6,7,9), n(1,3,5,6,7,8,9), n(1,3,5,7,8,9), n(2,5,8), g(4), n(2,6,8)],
      [g(1), g(2), n(3,7), n(4,6,7), n(3,4,6,7,8), n(3,4,7,8), e([7,8]), g(9), g(5)],
      [g(8), n(3,7), g(5), n(1,7,9), n(1,3,7,9), n(1,3,7,9), g(6), g(2), g(4)],
      [g(9), g(6), g(4), e([5,7]), e([2,5,7,8]), e([2,5,7,8]), g(3), g(1), n(7,8)],
      [e([4,7]), e([1,4,7,8,9]), n(7,8,9), e([1,4,7,9]), e([1,2,4,7,9]), g(6), n(2,7), g(5), g(3)],
      [g(5), n(1,3,4,7), n(3,7), g(8), n(1,2,4,7), n(1,2,4,7), g(9), g(6), e([2,7])],
      [g(6), n(7,9), g(2), g(3), n(5,7,9), n(5,7,9), g(4), g(8), g(1)],
    ],
  },
  'exocet': {
    data: [
      [g(9), g(2), g(5), n(6,7), g(1), n(6,7), g(8), g(4), g(3)],
      [g(1), g(3), g(4), n(2,8), g(5), n(2,8), g(6), g(7), g(9)],
      [g(6), g(7), g(8), g(3), g(4), g(9), g(1), g(2), g(5)],
      [g(2), n(4,9), g(1), n(7,9), g(6), g(3), n(4,7), g(5), g(8)],
      [f(7,8), f(4,8), g(6), g(5), g(2), g(1), g(3), g(9), n(4,7)],
      [n(3,7), g(5), n(3,9), n(7,8,9), e([7,8,9],8), g(4), g(2), g(1), g(6)],
      [g(4), n(6,9), n(2,3,9), g(1), n(3,7,9), n(2,5,6), n(5,7), g(8), n(2,7)],
      [g(5), g(1), g(7), n(2,4,6,8), n(3,8), n(2,6,8), g(9), n(3,6), n(2,4)],
      [n(3,8), n(6,8,9), n(2,3,9), n(2,4,6,7,9), n(3,7,9), n(2,5,6,7), n(4,5,7), n(3,6), g(1)],
    ],
  },
  'bowmans-bingo': {
    data: [
      [g(3), n(4,8,9), g(1), g(2), n(4,5,6,8,9), n(4,5,8,9), n(5,8), g(7), n(6,8)],
      [g(2), g(5), n(6,7,8), n(4,6,7), n(4,6,7,8), n(4,7,8), g(1), g(3), g(9)],
      [n(7), n(7,8,9), n(6,7,8,9), n(1,5,6,7,9), n(1,3,5,6,7,8,9), n(1,3,5,7,8,9), n(2,5,8), g(4), n(2,6,8)],
      [g(1), g(2), n(3,7), n(4,6,7), n(3,4,6,7,8), n(3,4,7,8), n(7,8), g(9), g(5)],
      [g(8), n(3,7), g(5), n(1,7,9), n(1,3,7,9), n(1,3,7,9), g(6), g(2), g(4)],
      [g(9), g(6), g(4), n(5,7), n(2,5,7,8), n(2,5,7,8), g(3), g(1), n(7,8)],
      [n(4,7), n(1,4,7,8,9), n(7,8,9), n(1,4,7,9), n(1,2,4,7,9), g(6), n(2,7), g(5), g(3)],
      [g(5), n(1,3,4,7), n(3,7), g(8), n(1,2,4,7), n(1,2,4,7), g(9), g(6), n(2,7)],
      [g(6), n(7,9), g(2), g(3), n(5,7,9), n(5,7,9), g(4), g(8), g(1)],
    ],
  },
};
